import React, { useState, useEffect } from 'react';
import { Container, Box, Typography, Button, Dialog, DialogTitle, DialogContent, DialogActions, List, ListItem, ListItemIcon, ListItemText } from '@mui/material';
import InputForm from '../components/salaryCalculator/Inputs/SalaryCalculator/InputForm';
import TaxVisualization from '../components/salaryCalculator/Visualisations/SalaryCalculator/TaxVisualisation';
import { calculateAll } from '../calculations/taxCalculations';
import OutputContainer from '../components/salaryCalculator/Outputs/OutputContainer';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { Helmet } from 'react-helmet';
import { trackEvent } from "../analytics";
import { useConfig } from '../context/ConfigContext';
import { WebApplicationSchema, BreadcrumbSchema } from '../components/StructuredData';
import LeadForm from '../components/LeadForm';
import { isFeatureEnabled } from '../featureFlags';

const SalaryCalculator = () => {
  const { config } = useConfig();

  const [userInput, setUserInput] = useState({
    salary: 100000,
    overtime: { include: false, value: 0, inputType: 'amount' },
    pension: {
      active: false,
      contribution: 5,
      inputType: 'percentage',
      scheme: 'Auto-enrolment',
      calculationBasis: 'Qualifying Earnings',
      includeOvertime: false,
    },
    studentLoanPlan: { plan: "none" }
  });
  
  const [results, setResults] = useState(() => {
    return calculateAll(userInput, config);
  });
  const taxBands = config.TAX_BANDS;
  const maxSalary = config.MAX_SALARY;

  useEffect(() => {
    const calcResults = calculateAll(userInput, config);
    setResults(calcResults);
  }, [userInput, config]);

  // State to control the How It Works modal
  const [showHowItWorks, setShowHowItWorks] = useState(false);

  const handleOpenModal = () => {
    setShowHowItWorks(true);
  };

  const handleCloseModal = () => {
    setShowHowItWorks(false);
  };

  const handleLearnMoreClick = () => {
    trackEvent("Learn More", "Click", "Learn more about how this tool works.");
  };

  return (
    <Container>
      <Helmet>
        <title>UK Take Home Pay Calculator | SalaryTools.co.uk</title>
        <meta
          name="description"
          content="Calculate your UK take home pay with our interactive calculator. Adjust your salary, overtime, pension contributions, and student loan repayments to see a detailed breakdown of your financial picture."
        />
        <link rel="canonical" href="https://salarytools.co.uk/salary-calculator" />
      </Helmet>
      <WebApplicationSchema />
      <BreadcrumbSchema 
        items={[
          { name: "Home", url: "https://salarytools.co.uk" },
          { name: "Salary Calculator", url: "https://salarytools.co.uk/salary-calculator" }
        ]}
      />

      {/* Introduction Section */}
      <Box sx={{ pt: 8, mb: 6, textAlign: 'center' }}>
        <Typography variant="h1" component="h1" sx={{ 
          fontSize: { xs: '2.5rem', md: '3.5rem' },
          fontWeight: 'bold',
          mb: 2
        }}>
          UK Take Home Pay Calculator
        </Typography>
        <Typography variant="h5" component="h2" sx={{ 
          mb: 3,
          color: 'text.secondary',
          maxWidth: '800px',
          mx: 'auto'
        }}>
          Calculate your exact take-home pay with detailed tax breakdowns, 
          National Insurance contributions, and interactive visual bands
        </Typography>
        <Typography variant="body1" sx={{ 
          maxWidth: '600px',
          mx: 'auto',
          color: 'text.secondary',
          mb: 2
        }}>
          Enter your salary and customize with overtime, pension contributions, student loans, 
          and residency details for a comprehensive breakdown of your UK tax position.
        </Typography>
        <Typography
          variant="body1"
          sx={{
            color: 'primary.main',
            textDecoration: 'underline',
            cursor: 'pointer'
          }}
          onClick={() => { handleOpenModal(); handleLearnMoreClick(); }}
        >
          Learn more about how this tool works.
        </Typography>
      </Box>

      <InputForm onInputChange={setUserInput} config={config}/>

      <TaxVisualization
        bracketDetails={results.bracketDetails}
        maxSalary={maxSalary}
        taxBands={taxBands}
        salary={results.totalGrossIncome}
        taxableIncome={results.taxableIncome}
        taxPaid={results.taxPaid}
        takeHome={results.takeHome}
        effectiveTaxRate={results.effectiveTaxRate}
        nationalInsurance={results.nationalInsurance}
        darkMode={false}
      />
      <Box sx={{ mb: 8 }}>
        <OutputContainer results={results} salary={results.totalGrossIncome} config={config} />
      </Box>

      {/* Lead Form Section */}
      {isFeatureEnabled('LEAD_FORM') && (
        <Box sx={{ mt: 6, p: 4, bgcolor: 'grey.50', borderRadius: 2 }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography variant="h5" component="h2" gutterBottom>
              Want More Salary Insights?
            </Typography>
            <Typography variant="body1" color="text.secondary">
              Get notified about new features and UK tax updates that could affect your take-home pay
            </Typography>
          </Box>
          <Box sx={{ maxWidth: 400, mx: 'auto' }}>
            <LeadForm 
              title="Stay Informed" 
              subtitle="Join our community of UK professionals maximizing their salaries"
            />
          </Box>
        </Box>
      )}

      {/* How It Works Modal */}
      <Dialog open={showHowItWorks} onClose={handleCloseModal} fullWidth maxWidth="md">
        <DialogTitle>How the Calculator Works</DialogTitle>
        <DialogContent dividers>
          <Typography variant="body1" sx={{ mb: 2 }}>
            Our calculator processes your input in several steps to provide you with a detailed breakdown of your take-home pay:
          </Typography>
          <List>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="We start with your base salary and add any overtime earnings to determine your total gross income." />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="If you contribute to a pension, we deduct your contribution from your gross income, referencing the options you've chosen. For personal pension schemes, your net contribution is recalculated to estimate the corresponding gross amount." />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Tax is computed using current tax bands. If your income exceeds the personal allowance threshold, the calculator adjusts your allowance and applies the correct rates across different brackets." />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="National Insurance and student loan repayments are calculated based on your gross income, ensuring that all deductions are accurately reflected." />
            </ListItem>
            <ListItem>
              <ListItemIcon>
                <CheckCircleIcon color="primary" />
              </ListItemIcon>
              <ListItemText primary="Finally, by subtracting all these deductions from your total income, we arrive at your take-home pay – the amount you actually receive." />
            </ListItem>
          </List>
          <Typography variant="body1" sx={{ mt: 2 }}>
            Our goal is to provide you with clear insights into how each component of your income and deductions contributes to your final take-home pay.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default SalaryCalculator;
